import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { MapPin, Clock, Users, Globe, Star, ChevronLeft, ChevronRight, Check, X, HelpCircle, Calendar, Send, MessageCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import SEO from '../components/SEO';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PackageDetailPage = () => {
  const { id } = useParams();
  const [pkg, setPkg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [activeTab, setActiveTab] = useState('itinerary');
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [enquiryForm, setEnquiryForm] = useState({
    name: '', 
    origin: '',
    email: '', 
    phone: '', 
    adults: '2',
    children: '0',
    childrenAges: '',
    travel_date: '',
    travelInterest: '',
    hotelPreference: '3-star',
    approximateBudget: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [showMobileEnquiry, setShowMobileEnquiry] = useState(false);

  useEffect(() => {
    if (id) {
      fetch(`${API_URL}/api/packages/${id}`)
        .then(res => res.json())
        .then(data => {
          setPkg(data);
          setLoading(false);
        })
        .catch(() => setLoading(false));
    }
  }, [id]);

  const handleEnquirySubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    // Build comprehensive message
    let fullMessage = `PACKAGE ENQUIRY DETAILS\n${'─'.repeat(40)}\n`;
    fullMessage += `Package: ${pkg?.title}\n`;
    fullMessage += `Destination: ${pkg?.destination}\n`;
    fullMessage += `Duration: ${pkg?.duration_nights}N / ${pkg?.duration_days}D\n`;
    fullMessage += `${'─'.repeat(40)}\n`;
    fullMessage += `Origin: ${enquiryForm.origin || 'Not specified'}\n`;
    fullMessage += `Travelers: ${enquiryForm.adults} Adults`;
    if (parseInt(enquiryForm.children) > 0) {
      fullMessage += `, ${enquiryForm.children} Children`;
      if (enquiryForm.childrenAges) fullMessage += ` (Ages: ${enquiryForm.childrenAges})`;
    }
    fullMessage += `\nPreferred Travel Date: ${enquiryForm.travel_date || 'Flexible'}\n`;
    fullMessage += `Travel Interest: ${enquiryForm.travelInterest || 'Not specified'}\n`;
    fullMessage += `Hotel Preference: ${enquiryForm.hotelPreference}\n`;
    fullMessage += `Approximate Budget: ${enquiryForm.approximateBudget || 'Not specified'}\n`;
    if (enquiryForm.message) fullMessage += `\nAdditional Notes:\n${enquiryForm.message}`;
    
    try {
      await fetch(`${API_URL}/api/enquiries`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: enquiryForm.name,
          email: enquiryForm.email,
          phone: enquiryForm.phone,
          enquiry_type: 'package',
          package_id: id,
          message: fullMessage
        })
      });
      setSubmitted(true);
    } catch (err) {
      console.error('Enquiry error:', err);
    }
    setSubmitting(false);
  };

  // Combine main image with gallery images, avoiding duplicates
  const allImages = pkg?.images?.length > 0 
    ? [pkg.image, ...pkg.images.filter(img => img !== pkg.image)] 
    : [pkg?.image].filter(Boolean);

  if (loading) {
    return (
      <div className="min-h-screen bg-s-bg flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-s-primary"></div>
      </div>
    );
  }

  if (!pkg) {
    return (
      <div className="min-h-screen bg-s-bg flex flex-col items-center justify-center gap-4">
        <h1 className="text-2xl font-bold text-s-text">Package Not Found</h1>
        <Link to="/packages">
          <Button>Browse All Packages</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-s-bg pt-16 md:pt-20">
      <SEO title={`${pkg.title} | Sentora Holidays`} description={pkg.brief || `Explore ${pkg.title} - ${pkg.duration_nights}N/${pkg.duration_days}D tour package`} />

      {/* Header */}
      <section className="bg-s-bg2 border-b border-s-border py-6 md:py-8">
        <div className="max-w-7xl mx-auto px-4">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-sm text-s-text2 mb-4">
            <Link to="/" className="hover:text-s-primary">Home</Link>
            <span>/</span>
            <Link to="/packages" className="hover:text-s-primary">Packages</Link>
            <span>/</span>
            <span className="text-s-text">{pkg.title}</span>
          </div>

          {/* Title */}
          <h1 className="text-2xl md:text-4xl font-bold text-s-text mb-4 font-heritage">{pkg.title}</h1>

          {/* Location Bar - Wraps properly */}
          <div className="flex flex-wrap items-start gap-2 text-s-text2 mb-4 max-w-3xl">
            <MapPin size={18} className="text-s-primary flex-shrink-0 mt-0.5" />
            <span className="font-medium flex-shrink-0">{pkg.state}</span>
            <span className="text-s-muted flex-shrink-0">•</span>
            <span className="break-words">{pkg.cities?.join(' → ') || pkg.destination}</span>
          </div>

          {/* Key Attributes - Only Duration */}
          <div className="flex flex-wrap gap-4">
            <Badge className="bg-s-primary/10 text-s-primary border-s-primary/30 px-3 py-1">
              <Clock size={14} className="mr-1" />
              {pkg.duration_nights}N / {pkg.duration_days}D
            </Badge>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Content - 2/3 */}
          <div className="lg:col-span-2 space-y-8">
            {/* Image Gallery */}
            <div className="relative rounded-2xl overflow-hidden">
              <div className="aspect-video relative">
                <img
                  src={allImages[currentImageIndex]}
                  alt={`${pkg.title} - Image ${currentImageIndex + 1}`}
                  className="w-full h-full object-cover"
                />
                {allImages.length > 1 && (
                  <>
                    <button
                      onClick={() => setCurrentImageIndex((prev) => (prev === 0 ? allImages.length - 1 : prev - 1))}
                      className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 p-2 rounded-full"
                    >
                      <ChevronLeft className="text-white" size={24} />
                    </button>
                    <button
                      onClick={() => setCurrentImageIndex((prev) => (prev === allImages.length - 1 ? 0 : prev + 1))}
                      className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 p-2 rounded-full"
                    >
                      <ChevronRight className="text-white" size={24} />
                    </button>
                  </>
                )}
              </div>
              {/* Thumbnail Strip */}
              {allImages.length > 1 && (
                <div className="flex gap-2 mt-2 overflow-x-auto py-2">
                  {allImages.map((img, idx) => (
                    <button
                      key={idx}
                      onClick={() => setCurrentImageIndex(idx)}
                      className={`flex-shrink-0 w-20 h-14 rounded-lg overflow-hidden border-2 transition-all ${idx === currentImageIndex ? 'border-s-primary' : 'border-transparent opacity-60 hover:opacity-100'}`}
                    >
                      <img loading="lazy" src={img} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Brief */}
            {pkg.brief && (
              <Card className="bg-s-card border-s-border">
                <CardContent className="p-6">
                  <h2 className="text-xl font-bold text-s-text mb-3">Overview</h2>
                  <p className="text-s-text2 leading-relaxed">{pkg.brief}</p>
                </CardContent>
              </Card>
            )}

            {/* Interesting Facts / About the Place */}
            {pkg.interesting_facts && (
              <Card className="bg-s-card border-s-border">
                <CardContent className="p-6">
                  <h2 className="text-xl font-bold text-s-text mb-3">About This Destination</h2>
                  <p className="text-s-text2 leading-relaxed whitespace-pre-line">{pkg.interesting_facts}</p>
                </CardContent>
              </Card>
            )}

            {/* Best Time to Visit */}
            {pkg.best_time_to_visit && (
              <Card className="bg-gradient-to-r from-s-primary/10 to-s-primary/10 border-s-primary/20">
                <CardContent className="p-4 flex items-center gap-3">
                  <Calendar className="text-s-primary flex-shrink-0" size={20} />
                  <div>
                    <span className="text-sm text-s-muted">Best Time to Visit</span>
                    <p className="text-s-text font-medium">{pkg.best_time_to_visit}</p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Tabs */}
            <div className="border-b border-s-border">
              <div className="flex gap-6">
                {['itinerary', 'highlights', 'faqs'].map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`pb-3 text-sm font-medium transition-colors relative ${activeTab === tab ? 'text-s-primary' : 'text-s-text2 hover:text-s-text'}`}
                  >
                    {tab === 'highlights' ? 'Key Highlights' : tab.charAt(0).toUpperCase() + tab.slice(1)}
                    {activeTab === tab && (
                      <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-s-primary" />
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Tab Content */}
            <div className="min-h-[400px]">
              {/* Itinerary Tab */}
              {activeTab === 'itinerary' && (
                <div className="space-y-0 py-4">
                  {pkg.itinerary?.length > 0 ? (
                    pkg.itinerary.map((day, idx) => (
                      <div key={idx} className="relative">
                        {/* Day Card with clear separation */}
                        <div className="bg-s-card border border-s-border rounded-2xl p-5 md:p-6 mb-6 shadow-sm">
                          {/* Day Number Badge */}
                          <div className="absolute -top-3 left-6 bg-s-primary text-white px-4 py-1 rounded-full text-sm font-bold shadow-md">
                            Day {day.day || idx + 1}
                          </div>
                          
                          <div className="flex flex-col md:flex-row md:items-start gap-6 md:gap-8 mt-2">
                            {/* Left Side - Title ABOVE, Polaroid Photo BELOW (centered, top-aligned) */}
                            <div className="flex-shrink-0 w-full md:w-64 self-start">
                              {/* Day title */}
                              <h3 className="text-s-primary font-bold text-base md:text-lg uppercase tracking-wide text-center mb-4">
                                {day.title || 'Explore'}
                              </h3>
                              {/* Polaroid frame - CENTERED */}
                              <div className="flex justify-center">
                                <div className="relative">
                                  <div className="bg-white p-2 pb-3 shadow-lg transform hover:-rotate-1 transition-transform duration-300 w-52 md:w-56" style={{ boxShadow: '3px 3px 12px rgba(0,0,0,0.15)' }}>
                                    <div className="aspect-[4/3] overflow-hidden rounded-sm">
                                      <img
                                        src={day.image || pkg.image}
                                        alt={day.title || `Day ${day.day || idx + 1}`}
                                        className="w-full h-full object-cover"
                                      />
                                    </div>
                                  </div>
                                </div>
                              </div>
                              
                              {/* Decorative travel icons below the image */}
                              <div className="mt-6 flex flex-col items-center gap-4">
                                {/* Travel icons row */}
                                <div className="flex justify-center gap-3 text-s-primary/40">
                                  {[
                                    ['🌅', '🏛️', '🚗'][idx % 3],
                                    ['📸', '🗺️', '⛵'][idx % 3],
                                    ['🌴', '🏔️', '🌊'][idx % 3]
                                  ].map((emoji, i) => (
                                    <span key={i} className="text-2xl opacity-60 hover:opacity-100 transition-opacity">{emoji}</span>
                                  ))}
                                </div>
                                
                                {/* Day highlight badge - uses separate highlight field if available */}
                                {(day.highlight || day.title) && (
                                  <div className="bg-s-primary/10 rounded-lg px-4 py-2 text-center">
                                    <p className="text-xs text-s-muted uppercase tracking-wider">Day {day.day || idx + 1} Highlight</p>
                                    <p className="text-sm font-medium text-s-primary mt-1">
                                      {day.highlight || day.title?.split(',')[0] || 'Discover'}
                                    </p>
                                  </div>
                                )}
                                
                                {/* Decorative dots */}
                                <div className="flex gap-1.5">
                                  {[...Array(3)].map((_, i) => (
                                    <div key={i} className={`w-2 h-2 rounded-full ${i === idx % 3 ? 'bg-s-primary' : 'bg-s-primary/20'}`} />
                                  ))}
                                </div>
                              </div>
                            </div>

                            {/* Right Side - Activities (combined) */}
                            <div className="flex-1 relative pl-6 md:pl-8 border-l-2 border-s-primary/20">
                              <div className="space-y-3">
                                {/* Show combined activities or individual fields for backward compatibility */}
                                {day.activities ? (
                                  <p className="text-s-text text-sm md:text-base leading-relaxed whitespace-pre-line">{day.activities}</p>
                                ) : (
                                  <>
                                    {day.morning && <p className="text-s-text text-sm md:text-base leading-relaxed">{day.morning}</p>}
                                    {day.afternoon && <p className="text-s-text text-sm md:text-base leading-relaxed">{day.afternoon}</p>}
                                    {day.evening && <p className="text-s-text text-sm md:text-base leading-relaxed">{day.evening}</p>}
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        {/* Connector line between days */}
                        {idx < pkg.itinerary.length - 1 && (
                          <div className="flex justify-center -mt-3 mb-3">
                            <div className="w-0.5 h-6 bg-s-primary/30" />
                          </div>
                        )}
                      </div>
                    ))
                  ) : (
                    <p className="text-s-text2">Detailed itinerary coming soon...</p>
                  )}

                  {/* Best Time to Visit */}
                  {pkg.best_time_to_visit && (
                    <Card className="bg-s-accent/5 border-s-accent/20">
                      <CardContent className="p-4 md:p-6">
                        <div className="flex items-start gap-3">
                          <Calendar className="text-s-accent flex-shrink-0 mt-1" size={20} />
                          <div>
                            <h3 className="font-bold text-s-text mb-1">Best Time to Visit</h3>
                            <p className="text-s-text2">{pkg.best_time_to_visit}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>
              )}

              {/* Key Highlights Tab (formerly Inclusions) */}
              {activeTab === 'highlights' && (
                <div className="py-4">
                  <Card className="bg-s-card border-s-border">
                    <CardHeader>
                      <CardTitle className="text-s-primary flex items-center gap-2">
                        <Check size={20} /> Key Highlights
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ul className="grid md:grid-cols-2 gap-3">
                        {(pkg.inclusions || []).map((item, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-s-text2">
                            <Check size={16} className="text-s-primary flex-shrink-0 mt-1" />
                            {item}
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* FAQs Tab */}
              {activeTab === 'faqs' && (
                <div className="space-y-3">
                  {pkg.faqs?.length > 0 ? (
                    pkg.faqs.map((faq, idx) => (
                      <Card key={idx} className="bg-s-card border-s-border">
                        <button
                          onClick={() => setExpandedFaq(expandedFaq === idx ? null : idx)}
                          className="w-full p-4 text-left flex items-center justify-between"
                        >
                          <span className="font-medium text-s-text flex items-center gap-2">
                            <HelpCircle size={18} className="text-s-primary" />
                            {faq.question}
                          </span>
                          <ChevronRight className={`text-s-muted transition-transform ${expandedFaq === idx ? 'rotate-90' : ''}`} size={20} />
                        </button>
                        {expandedFaq === idx && (
                          <div className="px-4 pb-4">
                            <p className="text-s-text2 pl-7">{faq.answer}</p>
                          </div>
                        )}
                      </Card>
                    ))
                  ) : (
                    <p className="text-s-text2">FAQs coming soon...</p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Right Sidebar - Enquiry Form (Hidden on mobile, shown on lg+) */}
          <div className="hidden lg:block lg:col-span-1">
            <div className="sticky top-24">
              <Card className="bg-s-card border-s-border shadow-lg">
                <CardHeader className="bg-s-primary text-white rounded-t-lg">
                  <CardTitle className="text-center">Enquire Now</CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  {submitted ? (
                    <div className="text-center py-8">
                      <div className="w-16 h-16 bg-s-primary rounded-full flex items-center justify-center mx-auto mb-4">
                        <Check className="text-s-primary" size={32} />
                      </div>
                      <h3 className="text-lg font-bold text-s-text mb-2">Thank You!</h3>
                      <p className="text-s-text2">Our team will contact you shortly.</p>
                    </div>
                  ) : (
                    <form onSubmit={handleEnquirySubmit} className="space-y-3">
                      {/* Package Name - Auto captured */}
                      <div className="bg-s-bg/50 border border-s-border rounded-lg p-3">
                        <p className="text-xs text-s-muted mb-1">Enquiring for</p>
                        <p className="font-medium text-s-text text-sm">{pkg.title}</p>
                      </div>
                      
                      {/* Name & Origin */}
                      <div className="grid grid-cols-2 gap-2">
                        <Input
                          placeholder="Your Name *"
                          value={enquiryForm.name}
                          onChange={(e) => setEnquiryForm({...enquiryForm, name: e.target.value})}
                          required
                          className="bg-s-bg border-s-border text-sm"
                        />
                        <Input
                          placeholder="Origin City *"
                          value={enquiryForm.origin}
                          onChange={(e) => setEnquiryForm({...enquiryForm, origin: e.target.value})}
                          required
                          className="bg-s-bg border-s-border text-sm"
                        />
                      </div>
                      
                      {/* Email & Phone */}
                      <div className="grid grid-cols-2 gap-2">
                        <Input
                          type="email"
                          placeholder="Email *"
                          value={enquiryForm.email}
                          onChange={(e) => setEnquiryForm({...enquiryForm, email: e.target.value})}
                          required
                          className="bg-s-bg border-s-border text-sm"
                        />
                        <Input
                          type="tel"
                          placeholder="Phone *"
                          value={enquiryForm.phone}
                          onChange={(e) => setEnquiryForm({...enquiryForm, phone: e.target.value})}
                          required
                          className="bg-s-bg border-s-border text-sm"
                        />
                      </div>
                      
                      {/* Travelers */}
                      <div className="grid grid-cols-3 gap-2">
                        <div>
                          <label className="text-xs text-s-muted mb-1 block">Adults *</label>
                          <select 
                            value={enquiryForm.adults} 
                            onChange={(e) => setEnquiryForm({...enquiryForm, adults: e.target.value})} 
                            className="w-full h-9 px-2 rounded-md bg-s-bg border border-s-border text-s-text text-sm"
                          >
                            {[1,2,3,4,5,6,7,8,9,10].map(n => <option key={n} value={n}>{n}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className="text-xs text-s-muted mb-1 block">Children</label>
                          <select 
                            value={enquiryForm.children} 
                            onChange={(e) => setEnquiryForm({...enquiryForm, children: e.target.value})} 
                            className="w-full h-9 px-2 rounded-md bg-s-bg border border-s-border text-s-text text-sm"
                          >
                            {[0,1,2,3,4,5,6].map(n => <option key={n} value={n}>{n}</option>)}
                          </select>
                        </div>
                        {parseInt(enquiryForm.children) > 0 && (
                          <div>
                            <label className="text-xs text-s-muted mb-1 block">Ages</label>
                            <Input 
                              placeholder="e.g. 5, 8"
                              value={enquiryForm.childrenAges} 
                              onChange={(e) => setEnquiryForm({...enquiryForm, childrenAges: e.target.value})} 
                              className="bg-s-bg border-s-border text-sm h-9" 
                            />
                          </div>
                        )}
                      </div>
                      
                      {/* Travel Date */}
                      <div>
                        <label className="text-xs text-s-muted mb-1 block">Preferred Travel Date</label>
                        <Input
                          type="date"
                          value={enquiryForm.travel_date}
                          onChange={(e) => setEnquiryForm({...enquiryForm, travel_date: e.target.value})}
                          className="bg-s-bg border-s-border text-sm"
                        />
                      </div>
                      
                      {/* Travel Interest */}
                      <Input
                        placeholder="Specific Travel Interest (optional)"
                        value={enquiryForm.travelInterest}
                        onChange={(e) => setEnquiryForm({...enquiryForm, travelInterest: e.target.value})}
                        className="bg-s-bg border-s-border text-sm"
                      />
                      
                      {/* Hotel & Budget */}
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="text-xs text-s-muted mb-1 block">Hotel Preference</label>
                          <select 
                            value={enquiryForm.hotelPreference} 
                            onChange={(e) => setEnquiryForm({...enquiryForm, hotelPreference: e.target.value})} 
                            className="w-full h-9 px-2 rounded-md bg-s-bg border border-s-border text-s-text text-sm"
                          >
                            <option value="3-star">3 Star</option>
                            <option value="4-star">4 Star</option>
                            <option value="5-star">5 Star</option>
                            <option value="heritage">Heritage</option>
                            <option value="luxury">Luxury Resort</option>
                          </select>
                        </div>
                        <div>
                          <label className="text-xs text-s-muted mb-1 block">Approx. Budget</label>
                          <Input 
                            placeholder="e.g. ₹50,000"
                            value={enquiryForm.approximateBudget} 
                            onChange={(e) => setEnquiryForm({...enquiryForm, approximateBudget: e.target.value})} 
                            className="bg-s-bg border-s-border text-sm h-9" 
                          />
                        </div>
                      </div>
                      
                      {/* Additional Notes */}
                      <Textarea
                        placeholder="Additional requirements (optional)"
                        value={enquiryForm.message}
                        onChange={(e) => setEnquiryForm({...enquiryForm, message: e.target.value})}
                        rows={2}
                        className="bg-s-bg border-s-border text-sm"
                      />
                      
                      <Button
                        type="submit"
                        disabled={submitting}
                        className="w-full bg-s-primary hover:bg-s-primary/90 h-11"
                      >
                        {submitting ? 'Sending...' : (
                          <>
                            <Send size={18} className="mr-2" />
                            Send Enquiry
                          </>
                        )}
                      </Button>
                    </form>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Floating Enquiry Button for Mobile */}
      <div className="fixed bottom-6 right-6 lg:hidden z-50">
        <Button 
          onClick={() => setShowMobileEnquiry(true)}
          className="bg-s-primary hover:bg-s-primary-h text-white rounded-full w-14 h-14 shadow-xl flex items-center justify-center"
        >
          <MessageCircle size={24} />
        </Button>
      </div>

      {/* Mobile Enquiry Modal */}
      {showMobileEnquiry && (
        <div className="fixed inset-0 bg-black/60 z-50 lg:hidden flex items-end justify-center">
          <div className="bg-s-bg w-full max-h-[90vh] rounded-t-3xl overflow-y-auto animate-in slide-in-from-bottom">
            <div className="sticky top-0 bg-s-primary text-white p-4 flex items-center justify-between rounded-t-3xl">
              <h3 className="font-bold text-lg">Enquire Now</h3>
              <button onClick={() => setShowMobileEnquiry(false)} className="p-1 hover:bg-white/20 rounded-full">
                <X size={24} />
              </button>
            </div>
            <div className="p-4">
              {submitted ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-s-primary rounded-full flex items-center justify-center mx-auto mb-4">
                    <Check className="text-s-primary" size={32} />
                  </div>
                  <h3 className="text-lg font-bold text-s-text mb-2">Thank You!</h3>
                  <p className="text-s-text2">Our team will contact you shortly.</p>
                  <Button onClick={() => setShowMobileEnquiry(false)} className="mt-4 bg-s-primary">Close</Button>
                </div>
              ) : (
                <form onSubmit={handleEnquirySubmit} className="space-y-3">
                  <div className="bg-s-card border border-s-border rounded-lg p-3">
                    <p className="text-xs text-s-muted mb-1">Enquiring for</p>
                    <p className="font-medium text-s-text text-sm">{pkg.title}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Input placeholder="Your Name *" value={enquiryForm.name} onChange={(e) => setEnquiryForm({...enquiryForm, name: e.target.value})} required className="bg-s-card border-s-border text-sm" />
                    <Input placeholder="Origin City *" value={enquiryForm.origin} onChange={(e) => setEnquiryForm({...enquiryForm, origin: e.target.value})} required className="bg-s-card border-s-border text-sm" />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Input type="email" placeholder="Email *" value={enquiryForm.email} onChange={(e) => setEnquiryForm({...enquiryForm, email: e.target.value})} required className="bg-s-card border-s-border text-sm" />
                    <Input type="tel" placeholder="Phone *" value={enquiryForm.phone} onChange={(e) => setEnquiryForm({...enquiryForm, phone: e.target.value})} required className="bg-s-card border-s-border text-sm" />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs text-s-muted">Adults *</Label>
                      <Input type="number" min="1" value={enquiryForm.adults} onChange={(e) => setEnquiryForm({...enquiryForm, adults: e.target.value})} className="bg-s-card border-s-border text-sm" />
                    </div>
                    <div>
                      <Label className="text-xs text-s-muted">Children</Label>
                      <Input type="number" min="0" value={enquiryForm.children} onChange={(e) => setEnquiryForm({...enquiryForm, children: e.target.value})} className="bg-s-card border-s-border text-sm" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs text-s-muted">Tentative Travel Date</Label>
                    <Input type="date" value={enquiryForm.travelDate} onChange={(e) => setEnquiryForm({...enquiryForm, travelDate: e.target.value})} className="bg-s-card border-s-border text-sm" />
                  </div>
                  <Textarea placeholder="Any special requirements?" value={enquiryForm.message} onChange={(e) => setEnquiryForm({...enquiryForm, message: e.target.value})} className="bg-s-card border-s-border text-sm" rows={2} />
                  <Button type="submit" disabled={submitting} className="w-full bg-s-primary hover:bg-s-primary-h">
                    {submitting ? 'Submitting...' : 'Submit Enquiry'}
                  </Button>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PackageDetailPage;
